import React, { useContext, useEffect, useState } from 'react';
import { Web3Context } from '../../context/Web3Context';
import useContract from '../../hooks/useContract';
import { formatUSDT } from '../../utils/formatters';
import { POOL_NAMES, POOL_COLORS } from '../../utils/constants';
import Loader from '../../components/Loader/Loader';
import './Pools.css';

const POOL_CRITERIA = [
  'Packages 1-3 self active',
  'Packages 1-3 active + 3 directs with Packages 1-3 active',
  'Packages 1-5 active + 3 directs with Packages 1-5 active',
  'All 6 packages self active',
  'All 6 packages active + 3 directs with all 6 active',
  'All 6 packages active + 3 directs who are Blue eligible',
];

const Pools = () => {
  const { userId } = useContext(Web3Context);
  const { getPoolEligibilities, getClaimablePoolIncomes, claimPoolIncome, claimAllPoolIncomes, loading } = useContract();
  const [eligibilities, setEligibilities] = useState(null);
  const [claimable, setClaimable] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      if (!userId) return;
      setPageLoading(true);
      try {
        const [elig, claim] = await Promise.all([getPoolEligibilities(), getClaimablePoolIncomes()]);
        setEligibilities(elig);
        setClaimable(claim);
      } catch (err) { console.error(err); }
      finally { setPageLoading(false); }
    };
    fetchData();
  }, [userId]);

  const handleClaim = async (poolType) => {
    try {
      await claimPoolIncome(poolType);
      const [claim, elig] = await Promise.all([getClaimablePoolIncomes(), getPoolEligibilities()]);
      setClaimable(claim);
      setEligibilities(elig);
    } catch {}
  };

  if (pageLoading) return <Loader text="Loading Pools" />;

  const poolKeys = ['gold', 'platinum', 'diamond', 'crown', 'blue', 'ambassador'];
  const totalClaimable = poolKeys.reduce((sum, key) => sum + parseFloat(claimable?.[key] || '0'), 0);

  return (
    <div className="pools-page">
      <div className="page-header"><h1 className="page-title">Reward Pools</h1></div>

      {totalClaimable > 0 && (
        <div className="pool-claim-all">
          <div className="pca-info">
            <span className="pca-label">Total Pool Income</span>
            <span className="pca-value">{formatUSDT(String(totalClaimable))} USDT</span>
          </div>
          <button className="btn-claim" onClick={claimAllPoolIncomes} disabled={loading}>
            {loading ? 'Processing...' : 'Claim All'}
          </button>
        </div>
      )}

      <div className="pools-list">
        {POOL_NAMES.map((name, i) => {
          const key = poolKeys[i];
          const isEligible = eligibilities?.[key] || false;
          const claimableAmt = claimable?.[key] || '0';
          const hasIncome = parseFloat(claimableAmt) > 0;

          return (
            <div key={i} className={`pool-item ${isEligible ? 'eligible' : ''}`} style={{ '--pool-color': POOL_COLORS[i] }}>
              <div className="pi-top">
                <div className="pi-left">
                  <div className="pi-dot" style={{ background: isEligible ? POOL_COLORS[i] : 'rgba(255,255,255,0.1)' }}></div>
                  <div className="pi-info">
                    <span className="pi-name">{name}</span>
                    <span className={`pi-status ${isEligible ? 'yes' : 'no'}`}>{isEligible ? 'Eligible' : 'Not Eligible'}</span>
                  </div>
                </div>
                <div className="pi-right">
                  <span className="pi-amount" style={{ color: hasIncome ? POOL_COLORS[i] : 'rgba(184,228,255,0.3)' }}>{formatUSDT(claimableAmt)}</span>
                  <button className="pi-claim-btn" onClick={() => handleClaim(i + 1)} disabled={loading || !isEligible || !hasIncome} style={{ borderColor: `color-mix(in srgb, ${POOL_COLORS[i]} 30%, transparent)`, color: POOL_COLORS[i] }}>
                    Claim
                  </button>
                </div>
              </div>
              <div className="pi-criteria">
                <span className="pi-criteria-label">Criteria:</span> {POOL_CRITERIA[i]}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Pools;
